/**
 * Cloudflare-resized image URLs.
 *
 * Two hosts we control can resize on the fly:
 *   - Cloudflare Images: https://imagedelivery.net/<account>/<id>/<variant>
 *     where <variant> is a named variant ("public") or inline options
 *     ("w=240,fit=cover,format=auto").
 *   - Our media bucket behind the majourneys.com zone:
 *     https://cdn.majourneys.com/<path>  →  /cdn-cgi/image/<options>/<path>
 *
 *   - The hotel supplier's photos (static.cupid.travel), resized through the
 *     same zone: /cdn-cgi/image/<options>/https://static.cupid.travel/<path>.
 *     They arrive at 3000px and ~660 KB each, which left the Stays cards blank
 *     for seconds (owner, 2026-09-26). Needs that host in the zone's allowed
 *     transformation origins; until then the zone answers 403 and
 *     ImageWithFallback drops back to the plain URL.
 *
 * Everything else (Unsplash, Wikimedia, venue sites) is returned as-is: the
 * zone refuses to resize origins it has not been told to allow (403), so
 * wrapping them would waste a request.
 */

export type ImageFit = "scale-down" | "cover" | "contain";

export interface ResizeOptions {
  /** Cloudflare `fit` mode. Default `scale-down` (never upscales, never crops). */
  fit?: ImageFit;
}

const CF_IMAGES_HOST = "imagedelivery.net";
const CDN_HOST = "cdn.majourneys.com";
const CDN_RESIZE_PREFIX = "/cdn-cgi/image/";
/** Foreign hosts the majourneys.com zone is allowed to resize. */
const REMOTE_RESIZE_HOSTS = new Set(["static.cupid.travel"]);
const CDN_QUALITY = 80;

type ResizeHost = "cf-images" | "cdn" | "remote";

function parse(url: string): URL | null {
  try {
    return new URL(url);
  } catch {
    return null;
  }
}

function resizeHost(parsed: URL): ResizeHost | null {
  if (parsed.hostname === CF_IMAGES_HOST) {
    return parsed.pathname.split("/").filter(Boolean).length >= 2 ? "cf-images" : null;
  }
  if (parsed.hostname === CDN_HOST) {
    return parsed.pathname.startsWith(CDN_RESIZE_PREFIX) ? null : "cdn";
  }
  if (REMOTE_RESIZE_HOSTS.has(parsed.hostname) && parsed.protocol === "https:") return "remote";
  return null;
}

/** True when `resizedImageUrl` would actually change the URL. */
export function isResizableImageUrl(url: string): boolean {
  const parsed = parse(url);
  return parsed !== null && resizeHost(parsed) !== null;
}

/**
 * `url` resized to `width` CSS pixels, or `url` untouched when the host has no
 * resizer we control.
 */
export function resizedImageUrl(url: string, width: number, opts: ResizeOptions = {}): string {
  const fit = opts.fit ?? "scale-down";
  const parsed = parse(url);
  if (!parsed) return url;

  switch (resizeHost(parsed)) {
    case "cf-images": {
      const segments = parsed.pathname.split("/").filter(Boolean);
      const last = segments[segments.length - 1] ?? "";
      const options = last.includes("=")
        ? [`w=${width}`, ...last.split(",").filter((o) => !o.startsWith("w="))]
        : [`w=${width}`, `fit=${fit}`, "format=auto"];
      segments[segments.length - 1] = options.join(",");
      return `${parsed.origin}/${segments.join("/")}`;
    }
    case "cdn": {
      const path = parsed.pathname.replace(/^\/+/, "");
      const options = `width=${width},fit=${fit},format=auto,quality=${CDN_QUALITY}`;
      return `${parsed.origin}${CDN_RESIZE_PREFIX}${options}/${path}${parsed.search}`;
    }
    case "remote": {
      const options = `width=${width},fit=${fit},format=auto,quality=${CDN_QUALITY}`;
      return `https://${CDN_HOST}${CDN_RESIZE_PREFIX}${options}/${parsed.href}`;
    }
    default:
      return url;
  }
}

/**
 * `srcset` string with one `<url> <w>w` candidate per width, or `undefined`
 * when the host cannot be resized so callers can omit the attribute entirely.
 */
export function imageSrcSet(
  url: string,
  widths: readonly number[],
  opts: ResizeOptions = {},
): string | undefined {
  if (widths.length === 0 || !isResizableImageUrl(url)) return undefined;
  return widths.map((w) => `${resizedImageUrl(url, w, opts)} ${w}w`).join(", ");
}
